import React from 'react';

import {CopyClipboardInputField} from 'app/components/general/CopyClipboardInputField';
import {StyledModalFooter} from 'app/components/general/TabbedModal';
import {CheckboxButton} from 'app/general/widgets/CheckboxButton';
import {getContentType, getUnderlyingContentType} from 'app/utils/content-type';
import {documentEmbedCode, legacyDocumentEmbedCode} from 'app/utils/embed';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';

import type {BaseActionableDocumentType} from 'app/general/types';

type Props = {
  object: BaseActionableDocumentType;
  onCancel: () => void;
  onChange: (opts: {url: string; isPrivate: boolean}) => any;
  staticURL: string;
};

export function SharePublicModalPane({
  object,
  onChange,
  onCancel,
  staticURL,
}: Props): JSX.Element | null {
  const [asIframe, setAsIframe] = React.useState(false);

  function toggle(isPrivate: boolean): void {
    const doc = object.content_type === 'document' ? object.document : object;
    return onChange({
      url: doc.url,
      isPrivate: !isPrivate,
    });
  }

  const contentType = getUnderlyingContentType(object);

  if (object.content_type === 'proceduregroup') {
    return null;
  }

  const translationArgs = {
    name: <b>{getObjectName(object, contentType, true)}</b>,
    ct: getContentType(contentType).name,
  };

  if (contentType === 'quiz') {
    return null;
  }

  const doc = object.content_type === 'document' ? object.document : object;
  return (
    <div>
      <div className="form-group form-check">
        <CheckboxButton
          id="make-public-checkbox"
          className="form-check-input"
          onClick={toggle}
          isChecked={!doc.private}
          useRealCheckbox={true}
        />
        <div className="form-check-label">
          <label htmlFor="make-public-checkbox">
            {gt('Make the %(name)s %(ct)s public.', translationArgs)}
          </label>
          <div className="small">
            {gt(
              'Warning: Do not publish sensitive or private information in your public %s.',
              contentType
            )}
          </div>
        </div>
      </div>

      <CopyClipboardInputField
        disabled={doc.private === true}
        id={'public-copy-' + doc.id}
        label={gt('Public URL')}
        value={object.html_url}
      />

      <CopyClipboardInputField
        id={'embed-copy-' + doc.id}
        disabled={doc.private === true}
        label={gt('Embed this %(ct)s on your website', translationArgs)}
        value={documentEmbedCode(doc, contentType, staticURL)}
      />

      <div className="d-none d-sm-block">
        {!asIframe ? (
          <button
            className="btn btn-link pl-0 mb-1"
            onClick={() => setAsIframe(true)}
          >
            {gt('Embed as iframe (legacy)')} <i className="fa fa-caret-down" />
          </button>
        ) : (
          <CopyClipboardInputField
            id={'legacy-embed-copy-' + doc.id}
            disabled={doc.private === true}
            label={gt('Embed with an iframe (legacy)', translationArgs)}
            value={legacyDocumentEmbedCode(doc, contentType)}
          />
        )}
      </div>

      <StyledModalFooter>
        <a onClick={onCancel} className="btn btn-link" data-dismiss="modal">
          <i className="fa fa-times" /> {gt('Close')}
        </a>
      </StyledModalFooter>
    </div>
  );
}
